import { LoadingWrapper } from "components/common/layouts";
import FeedbackIcon from "components/common/ui/FeedbackIcon";
import { LIMIT_RECORDS_PER_REQUEST } from "constants/pagination.constant";
import { FormType } from "enums/form";
import { useError, useHttpPaginatedQueryService, useHttpQueryService } from "hooks";
import { ReactNode, useCallback } from "react";
import { FeedbackService } from "services";
import { FormWithQuestion } from "types/form";
import { User } from "types/user";
import ConsolidatedSectionHeader from "./ConsolidatedSectionHeader";
import FeedbackCard from "./FeedbackCard";
import { useConsolidatedDrawer } from "./ConsolidatedDrawerContext";

export type ConsolidatedFeedbackSheetProps = {
  revieweeId: number;
  forms: FormWithQuestion[];
  showViewMore: boolean;
  isFetchingNextPage: boolean;
  onViewMore: () => void;
};

type FeedbackConsolidatedSectionProps = {
  reviewee: User;
  formType: FormType;
  title: string;
  previewTake: number;
  showReviewer?: boolean;
  freeTextOnly?: boolean;
  renderSheet: (props: ConsolidatedFeedbackSheetProps) => ReactNode;
};

const FeedbackConsolidatedSection = ({
  reviewee,
  formType,
  title,
  previewTake,
  showReviewer = true,
  freeTextOnly = false,
  renderSheet,
}: FeedbackConsolidatedSectionProps) => {
  const { id: revieweeId, fullName: revieweeName } = reviewee;
  const { openDrawer } = useConsolidatedDrawer<number>();

  const feedbacksRequest = useCallback(() => {
    return FeedbackService.getConsolidatedFeedbacks(revieweeId, {
      formType,
      take: previewTake,
      skip: 0,
    });
  }, [revieweeId, formType, previewTake]);

  const {
    result: feedbacks,
    isLoading,
    error,
  } = useHttpQueryService({
    request: feedbacksRequest,
    deps: [revieweeId],
  });

  const {
    items: formList,
    hasMore: hasMoreForms,
    isFetchingNextPage: isFetchingNextPageForms,
    error: formsError,
    loadMore: loadMoreForms,
  } = useHttpPaginatedQueryService({
    fetchPage: (query) =>
      FeedbackService.getConsolidatedForms(revieweeId, {
        formType,
        ...query,
      }),
    take: LIMIT_RECORDS_PER_REQUEST,
    deps: [revieweeId],
  });

  useError(error, true);
  useError(formsError, true);

  const feedbackList = feedbacks ?? [];

  const getFormName = (formId: number) =>
    formList.find((form) => form.id === formId)?.name ?? "";

  return (
    <>
      <ConsolidatedSectionHeader
        className="mt-10"
        icon={<FeedbackIcon type={formType} />}
        title={title}
        onViewMore={() => {
          const firstFeedback = feedbackList[0];
          if (firstFeedback) {
            openDrawer(firstFeedback.formId, { scrollToList: true });
          }
        }}
      />

      <LoadingWrapper isLoading={isLoading} className="max-h-6">
        {feedbackList.length === 0 ? (
          <p className="body-14-regular">
            No data found for {revieweeName}.
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-6 xl:grid-cols-3 [&>*:nth-child(n+3)]:hidden xl:[&>*:nth-child(n+3)]:block">
            {feedbackList.map((feedback) => (
              <FeedbackCard
                key={feedback.id}
                feedback={feedback}
                formName={getFormName(feedback.formId)}
                showReviewer={showReviewer}
                freeTextOnly={freeTextOnly}
                onViewDetails={() => openDrawer(feedback.formId)}
              />
            ))}
          </div>
        )}
      </LoadingWrapper>

      {renderSheet({
        revieweeId,
        forms: formList,
        showViewMore: hasMoreForms,
        isFetchingNextPage: isFetchingNextPageForms,
        onViewMore: loadMoreForms,
      })}
    </>
  );
};

export default FeedbackConsolidatedSection;
